import { useState, useEffect } from 'react';
import { getMultiselectValues } from '../helpers/multiselect';
import styles from './CrudPanel.module.css';

const FormField = ({ field, value, onChange, onBlur, error, isChanged }) => {
    const [localValue, setLocalValue] = useState(value);
    
    // Sync local value with prop changes
    useEffect(() => {
        setLocalValue(value);
    }, [value]);
    
    const handleChange = (newValue) => {
        setLocalValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
    };
    
    const handleBlur = () => {
        if (onBlur) {
            onBlur();
        }
    };
    
    const placeholder = field.examples ? `e.g. ${field.examples.join(', ')}` : '';
    const hasError = !!error;
    const fieldId = `field-${field.key || field.title}`;
    
    // Render appropriate input based on field type
    const renderInput = () => {
        if (field.type === 'boolean') {
            return (
                <div className={styles.checkboxWrapper}>
                    <input
                        id={fieldId}
                        name={field.title}
                        type="checkbox"
                        checked={localValue === true || localValue === 'true'}
                        onChange={(e) => handleChange(e.target.checked)}
                        onBlur={handleBlur}
                        className={hasError ? styles.inputError : ''}
                    />
                    <label htmlFor={fieldId} className={styles.checkboxLabel}>
                        {field.title}
                    </label>
                </div>
            );
        }
        
        if (field.type === 'string' && !field.enum) {
            return (
                <input
                    id={fieldId}
                    name={field.title}
                    type="text"
                    placeholder={placeholder}
                    value={localValue || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    required={field.required}
                    className={hasError ? styles.inputError : ''}
                />
            );
        }
        
        if (field.type === 'integer' && !field.enum) {
            return (
                <input
                    id={fieldId}
                    name={field.title}
                    type="number"
                    placeholder={placeholder}
                    value={localValue ?? ''}
                    onChange={(e) => {
                        const numValue = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                        handleChange(isNaN(numValue) ? undefined : numValue);
                    }}
                    onBlur={handleBlur}
                    required={field.required}
                    className={hasError ? styles.inputError : ''}
                />
            );
        }
        
        if (field.enum && field.type === 'string') {
            return (
                <select
                    id={fieldId}
                    name={field.title}
                    value={localValue || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    required={field.required}
                    className={hasError ? styles.inputError : ''}
                >
                    <option disabled value="">--select an option--</option>
                    {field.enum.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            );
        }
        
        if (field.enum && field.type === 'integer') {
            return (
                <select
                    id={fieldId}
                    name={field.title}
                    value={localValue ?? ''}
                    onChange={(e) => {
                        const numValue = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                        handleChange(isNaN(numValue) ? undefined : numValue);
                    }}
                    onBlur={handleBlur}
                    required={field.required}
                    className={hasError ? styles.inputError : ''}
                >
                    <option disabled value="">--select an option--</option>
                    {field.enum.map(opt => {
                        const re = new RegExp(`${opt}\\s*=\\s*([^\\.,;]*)`);
                        const match = field.description?.match(re);
                        const text = match ? match[1] : opt;
                        return (
                            <option key={opt} value={opt}>{text}</option>
                        );
                    })}
                </select>
            );
        }
        
        if (field.enum && field.type === 'array') {
            const selectedValues = Array.isArray(localValue) ? localValue : [];
            return (
                <select
                    id={fieldId}
                    name={field.title}
                    multiple
                    value={selectedValues.map(String)}
                    onChange={(e) => {
                        const newValues = getMultiselectValues(e.target);
                        handleChange(newValues);
                    }}
                    onBlur={handleBlur}
                    required={field.required}
                    className={hasError ? styles.inputError : ''}
                >
                    {field.enum.map(opt => (
                        <option key={opt} value={String(opt)}>{opt}</option>
                    ))}
                </select>
            );
        }
        
        if (field.type === 'file') {
            return (
                <input
                    id={fieldId}
                    name={field.title}
                    type="file"
                    onChange={(e) => {
                        // File inputs are handled differently - just trigger onChange
                        if (onChange) {
                            onChange(e.target.files[0] || null);
                        }
                    }}
                    onBlur={handleBlur}
                    className={hasError ? styles.inputError : ''}
                />
            );
        }
        
        if (field.type === 'files') {
            return (
                <input
                    id={fieldId}
                    name={field.title}
                    type="file"
                    multiple
                    onChange={(e) => {
                        if (onChange) {
                            onChange(Array.from(e.target.files || []));
                        }
                    }}
                    onBlur={handleBlur}
                    className={hasError ? styles.inputError : ''}
                />
            );
        }
        
        return (
            <span className={styles.unsupportedType}>
                Field type "{field.type}" is not supported at this time.
            </span>
        );
    };
    
    // For boolean fields, render differently (checkbox with label)
    if (field.type === 'boolean') {
        return (
            <div className={`${styles.formField} ${styles.formFieldBoolean}`}>
                <label htmlFor={fieldId} className={styles.fieldLabel}>
                    {field.title}
                    {field.required && <span className={styles.required}>*</span>}
                    {isChanged && (
                        <span className={styles.changeBadge} title="Field has been modified">
                            ⚠️
                        </span>
                    )}
                </label>
                <div className={styles.fieldInput}>
                    {renderInput()}
                </div>
                {error && (
                    <div className={styles.fieldError} role="alert">
                        {error}
                    </div>
                )}
            </div>
        );
    }
    
    // For all other field types
    return (
        <div className={styles.formField}>
            <label htmlFor={fieldId} className={styles.fieldLabel}>
                {field.title}
                {field.required && <span className={styles.required}>*</span>}
                {isChanged && (
                    <span className={styles.changeBadge} title="Field has been modified">
                        ⚠️
                    </span>
                )}
            </label>
            <div className={styles.fieldInput}>
                {renderInput()}
                {field.type === 'array' && Array.isArray(localValue) && localValue.length > 0 && (
                    <div className={styles.arrayValues}>
                        Selected: {localValue.join(', ')}
                    </div>
                )}
            </div>
            {error && (
                <div className={styles.fieldError} role="alert">
                    {error}
                </div>
            )}
        </div>
    );
};

export default FormField;
